import {
    Component,
    AfterViewChecked,
    AfterViewInit,
    OnInit,
    OnDestroy,
    OnChanges,
    SimpleChanges,
    Output,
    EventEmitter,
    Input,
    ViewChild,
    TemplateRef,
    NO_ERRORS_SCHEMA,
    NgZone,
    ElementRef,
} from '@angular/core';

import { FormsModule, UntypedFormArray } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { PSIC_COMPONENTS } from '@pacificspecialtyinsurancecompany/psic-ng-components';
import { ChoiceChipComponent } from './choice-chip/choice-chip.component';
import { ChatService, ChatMessage } from '../shared/chat.service';
import { CallSummaryService } from '../services/call-summary.service';
import { CallIntentService } from '../services/call-intent.service';
import { SidePanelService } from '../shared/side-panel/side-panel.service';

import { updateNotes, loadPolicyDetails } from '../store/policy/policy.actions';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { PolicyState } from '../store/policy/policy.reducer';
import { Store } from '@ngrx/store';
import { LoadingService } from '../shared/loading.service';
import { DynamicFormComponent } from '../shared/dynamic-form/dynamic-form.component';
import { selectRecentPolicies } from '../store/recent-policies/recent-policies.selector';
import { getAuthToken } from '../auth.interceptor';
import { PolicySnapshotService } from '../services/data-service/policy-snapshot.service';
import { ConnectService } from '../services/amazon-connect.service';
import { QuickSightChatService } from '../services/quicksight-chat.service';
import { ToastService } from '../shared/toast/toast.service';
import { Router } from '@angular/router';
import { ChatbotStateService } from '../shared/chatbot-state.service';
import { AnimationComponent } from '../shared/animation/animation.component';
import { APP_CONFIG, AppConfig } from 'src/app.config';
import { NotesService } from '../services/notes.service';
import { refreshDCPolicy } from '../store/policy/policy.actions';
import { Inject } from '@angular/core';
import { forkJoin, firstValueFrom, Subject, Subscription, takeUntil, switchMap, take, map } from 'rxjs';
import { CallerService } from '../caller.service';

@Component({
    selector: 'app-chatbot-widget',
    templateUrl: './chatbot-widget.component.html',
    styleUrls: ['./chatbot-widget.component.scss'],
    imports: [FormsModule, ...PSIC_COMPONENTS, MatIcon, ChoiceChipComponent, DynamicFormComponent, AnimationComponent],
    schemas: [NO_ERRORS_SCHEMA],
    providers: [SidePanelService],
})
export class ChatbotWidgetComponent implements OnInit, AfterViewChecked, OnDestroy, OnChanges {
    @Input() sessionId = '';
    @Input() isLandingPage = false;
    @Input() contactId = '';
    @Input() channel = 'voice';
    @Input() attributes: any = {};
    @Output() closeChatbot = new EventEmitter<void>();

    notes: any[] = [];
    totalRecords = 0;
    generatedSummaries: Array<{
        text: string;
        timestamp: string;
        callNumber?: string | null;
        callerNumber?: string | null;
        attributes?: any;
    }> = [];
    policyNumber: string = '';
    isOpen = true;
    isExpanded = false;

    // ── Resize sash ──────────────────────────────────────────────────────────
    readonly MIN_WIDTH = 354;
    get MAX_WIDTH() {
        return Math.floor(window.innerWidth * 0.5);
    }
    panelWidth = this.MIN_WIDTH;
    isDragging = false;
    private _dragStartX = 0;
    private _dragStartWidth = 0;
    private _dragContainer: HTMLElement | null = null; // cached ref, avoids querySelector on every frame
    private _dragLandingPage: HTMLElement | null = null; // cached landing page ref for live right-offset update
    private _dragOverlay: HTMLElement | null = null; // covers iframes during drag
    private _mouseMoveRef!: (e: MouseEvent) => void;
    private _mouseUpRef!: (e: MouseEvent) => void;
    private _touchMoveRef!: (e: TouchEvent) => void;
    private _touchEndRef!: () => void;
    // ─────────────────────────────────────────────────────────────────────────

    field1: any;
    userChatInput = '';
    userCallSummaryInput = '';
    showChat = false;
    summaryGenerated = false;
    messages: ChatMessage[] = [];
    callSummaryMessages: ChatMessage[] = [];
    isChatLoading = false;
    isSummaryLoading = false;
    isCallIntentLoading = false;
    callIntentResult: any = null;
    showReloadButton = false;
    callSummaryText = '';
    callSummaryTimestamp = '';
    isViewMode = false;
    sidePanelForm = new UntypedFormArray([]);
    sidePanelField: any = {};
    savedEditions: string[] = [];
    recentPolicies: { policyNumber: string; lineOfBusiness: string }[] = [];
    ivrSelectedRole = this.callerService.ivrSelectedRole;



    recentPolicyNumbers: string[] = [];

    @ViewChild('sidePanelContent') sidePanelContent!: TemplateRef<any>;
    @ViewChild('sidePanelFooter') sidePanelFooter!: TemplateRef<any>;

    reviewNotesField: any = {};
    reviewNotesForm = new UntypedFormArray([]);
    saveEditionDoneMap: { [policyNumber: string]: boolean } = {};
    @ViewChild('reviewPanelContent') reviewPanelContent!: TemplateRef<any>;
    @ViewChild('reviewPanelFooter') reviewPanelFooter!: TemplateRef<any>;

    get currentInput(): string {
        return this.showChat ? this.userChatInput : this.userCallSummaryInput;
    }

    set currentInput(value: string) {
        if (this.showChat) {
            this.userChatInput = value;
        } else {
            this.userCallSummaryInput = value;
        }
    }

    private shouldScroll = false;
    private widgetObserver: MutationObserver | null = null;
    private destroy$ = new Subject<void>();
    private combinedSubscription: Subscription;
    private policyDetails: any;
    private savedEditionValues: { [policyNumber: string]: { note: string; category: string; subCategory: string } } =
        {};

    constructor(
        private http: HttpClient,
        private chatService: ChatService,
        private callSummaryService: CallSummaryService,
        private callIntentService: CallIntentService,
        private sidePanelService: SidePanelService,
        private store: Store<{ policy: PolicyState }>,
        private policySnapshotService: PolicySnapshotService,
        private loadingService: LoadingService,
        private toastService: ToastService,
        private connectService: ConnectService,
        private quickSightChatService: QuickSightChatService,
        private router: Router,
        private chatbotStateService: ChatbotStateService,
        private ngZone: NgZone,
        private elRef: ElementRef,
        private notesService: NotesService,
        @Inject(APP_CONFIG) private config: AppConfig,
        private callerService: CallerService,

    ) {}

    private parseCustomerIntent(raw: any): Array<{ category: string; intents: string[] }> {
        if (typeof raw === 'string') {
            try {
                return JSON.parse(raw);
            } catch {
                return [];
            }
        }
        return raw ?? [];
    }

    ngOnInit(): void {
        this.store
            .select(selectRecentPolicies)
            .pipe(takeUntil(this.destroy$))
            .subscribe((policies) => {
                this.recentPolicies = policies;
                this.recentPolicyNumbers = policies.map((p) => p.policyNumber);
            });

        this.initializeMessages();
        this.initializeChoiceChipField();
        this.checkAmazonConnectWidget();
        this.setupWidgetObserver();

        this.isLandingPage = false;

        // If attributes were provided at component creation, initialize pre-escalation summary
        if (this.attributes && Object.keys(this.attributes).length > 0 && !this.summaryGenerated) {
            const policyNumber = this.attributes?.policyNumber;
            if (policyNumber) {
                this.store.dispatch(loadPolicyDetails({ policyNumber }));
            }

            const now = new Date();
            const timestamp = this.formatTimestamp(now);
            this.generatedSummaries.push({
                text:
                    this.attributes?.EscalationSummary ||
                    this.attributes?.escalationSummary ||
                    'Pre-escalation summary available.',
                timestamp,
                callerNumber:
                    this.attributes?.CustomerPhoneNumber ||
                    this.attributes?.CustomerNumber ||
                    this.attributes?.callerNumber ||
                    null,
                callNumber:
                    this.attributes?.callBackNumber ||
                    this.attributes?.CustomerNumber ||
                    this.attributes?.CustomerPhoneNumber ||
                    null,
                attributes: {
                    ...this.attributes,
                    contactChannel: this.attributes?.contactChannel || this.channel || 'voice',
                },
            });
            this.summaryGenerated = true;
        }

        this.connectService.onAttributesReceived = (attributes) => {
            const now = new Date();
            const summaryText =
                this.connectService.callSummary ||
                attributes?.EscalationSummary ||
                attributes?.escalationSummary ||
                'No escalation summary available.';
            this.generatedSummaries.push({
                text: summaryText,
                timestamp: `${now.toLocaleDateString()} | ${now.toLocaleTimeString()}`,
                callerNumber:
                    attributes?.CustomerPhoneNumber || attributes?.CustomerNumber || attributes?.callerNumber || null,
                callNumber:
                    attributes?.callBackNumber || attributes?.CustomerNumber || attributes?.CustomerPhoneNumber || null,
                attributes: {
                    ...attributes,
                    contactChannel:
                        attributes?.contactChannel || this.channel || this.connectService.channel || 'voice',
                },
            });
            this.summaryGenerated = true;

            if (this.connectService.callSummary) {
                this.callIntentService.getCallIntent(this.connectService.callSummary).subscribe({
                    next: (response) => {
                        const responseBody =
                            typeof response?.body === 'string' ? JSON.parse(response.body) : response?.body ?? response;
                        const customerIntent = this.parseCustomerIntent(responseBody?.customerIntent);
                        this.callIntentService.lastCustomerIntent = customerIntent;

                        this.router.navigate(['/call-intent'], { state: { customerIntent } });
                    },
                    error: (err) => console.error('Error fetching call intent:', err),
                });
            }
        };

        this.sidePanelService.closeClicked.pipe(takeUntil(this.destroy$)).subscribe(() => this.cancelReviewNotes());
        this.sidePanelService.cancelClicked.pipe(takeUntil(this.destroy$)).subscribe(() => this.cancelReviewNotes());
        this.sidePanelService.saveClicked.pipe(takeUntil(this.destroy$)).subscribe(() => this.onSaveReviewNotes());
    }

    ngAfterViewInit(): void {
        // Set initial width directly on the DOM — bypasses Angular binding conflicts
        const container = this.elRef.nativeElement.querySelector('.chatbot-outer-container') as HTMLElement;
        if (container) container.style.width = this.MIN_WIDTH + 'px';
    }

    ngAfterViewChecked(): void {
        if (this.shouldScroll) {
            this.scrollToBottom();
            this.shouldScroll = false;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['attributes'] && changes['attributes'].currentValue) {
            const attrs = changes['attributes'].currentValue;
            const now = new Date();
            const timestamp = this.formatTimestamp(now);

            if (!this.summaryGenerated && (!this.generatedSummaries || this.generatedSummaries.length === 0)) {
                this.generatedSummaries.push({
                    text: attrs?.EscalationSummary || attrs?.escalationSummary || 'Pre-escalation summary available.',
                    timestamp,
                    callerNumber: attrs?.CustomerPhoneNumber || attrs?.CustomerNumber || attrs?.callerNumber || null,
                    callNumber: attrs?.callBackNumber || attrs?.CustomerNumber || attrs?.CustomerPhoneNumber || null,
                    attributes: {
                        ...attrs,
                        contactChannel: attrs?.contactChannel || this.channel || 'voice',
                    },
                });
                this.summaryGenerated = true;
            } else if (this.generatedSummaries && this.generatedSummaries.length > 0) {
                // Update first summary attributes/text when new attributes arrive
                const first = this.generatedSummaries[0];
                first.attributes = {
                    ...attrs,
                    contactChannel:
                        attrs?.contactChannel || this.channel || first.attributes?.contactChannel || 'voice',
                };
                if (attrs?.EscalationSummary || attrs?.escalationSummary) {
                    first.text = attrs?.EscalationSummary || attrs?.escalationSummary;
                }
            }
        }
    }

    ngOnDestroy(): void {
        if (this.widgetObserver) {
            this.widgetObserver.disconnect();
        }
        // Clean up QuickSight chat
        this.quickSightChatService.destroy();
        // Clean up event listeners if component is destroyed mid-drag
        if (this.isDragging) {
            document.removeEventListener('mousemove', this._mouseMoveRef);
            document.removeEventListener('mouseup', this._mouseUpRef);
            document.removeEventListener('touchmove', this._touchMoveRef);
            document.removeEventListener('touchend', this._touchEndRef);
        }
        // Clean up drag overlay if component is destroyed mid-drag
        if (this._dragOverlay && this._dragOverlay.parentNode) {
            document.body.removeChild(this._dragOverlay);
            this._dragOverlay = null;
        }
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
        this.destroy$.next();
        this.destroy$.complete();
    }

    onClose(): void {
        this.closeChatbot.emit();
    }

    // ── Resize sash ──────────────────────────────────────────────────────────

    onSashMouseDown(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this._beginDrag(event.clientX);

        this._mouseMoveRef = (e: MouseEvent) => this._moveDrag(e.clientX);
        this._mouseUpRef = () => this._commitDrag();

        this.ngZone.runOutsideAngular(() => {
            document.addEventListener('mousemove', this._mouseMoveRef);
            document.addEventListener('mouseup', this._mouseUpRef);
        });
    }

    onSashTouchStart(event: TouchEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this._beginDrag(event.touches[0].clientX);

        this._touchMoveRef = (e: TouchEvent) => this._moveDrag(e.touches[0].clientX);
        this._touchEndRef = () => this._commitDrag();

        this.ngZone.runOutsideAngular(() => {
            document.addEventListener('touchmove', this._touchMoveRef, { passive: false });
            document.addEventListener('touchend', this._touchEndRef);
        });
    }

    /** Selectors for all page containers that must shrink when the chat panel resizes */
    private readonly PAGE_CONTAINER_SELECTORS = [
        '.warning-banner',
        '.landing-page',
        '.policy-snapshot-container',
        '.profile-activities-container',
        '.call-intent-container',
    ];

    /** Write the chatbot width as a CSS custom property on :root so all containers can react */
    private _applyWidthToPageContainers(newWidth: number): void {
        // Set CSS variable on root — used by :host-context rules as fallback
        document.documentElement.style.setProperty('--chatbot-width', newWidth + 'px');

        // Also write right directly to any currently-rendered page container
        for (const selector of this.PAGE_CONTAINER_SELECTORS) {
            const el = document.querySelector(selector) as HTMLElement | null;
            if (el) el.style.right = newWidth + 'px';
        }
    }

    private _beginDrag(clientX: number): void {
        this._dragStartX = clientX;
        this._dragStartWidth = this.panelWidth;
        this.isDragging = true;

        // Cache the chatbot container — avoids querySelector on every mousemove frame
        this._dragContainer = this.elRef.nativeElement.querySelector('.chatbot-outer-container') as HTMLElement;
        if (this._dragContainer) this._dragContainer.style.transition = 'none';

        // Disable transitions on all page containers for instant feedback
        for (const selector of this.PAGE_CONTAINER_SELECTORS) {
            const el = document.querySelector(selector) as HTMLElement | null;
            if (el) el.style.transition = 'none';
        }

        // Keep legacy ref for _commitDrag cleanup
        this._dragLandingPage = document.querySelector('.landing-page') as HTMLElement;

        // Full-viewport overlay — prevents Amazon Connect iframe from swallowing mouse events
        this._dragOverlay = document.createElement('div');
        this._dragOverlay.style.cssText =
            'position:fixed;top:0;left:0;width:100vw;height:100vh;z-index:99999;cursor:col-resize;';
        document.body.appendChild(this._dragOverlay);

        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'col-resize';
    }

    private _moveDrag(clientX: number): void {
        // Panel anchored to right → dragging LEFT increases width
        const delta = this._dragStartX - clientX;
        const newWidth = Math.min(Math.max(this._dragStartWidth + delta, this.MIN_WIDTH), this.MAX_WIDTH);

        // Update chatbot container width
        if (this._dragContainer) this._dragContainer.style.width = newWidth + 'px';

        // Update all page containers simultaneously — zero Angular CD overhead
        this._applyWidthToPageContainers(newWidth);

        this.panelWidth = newWidth;
    }

    private _commitDrag(): void {
        document.removeEventListener('mousemove', this._mouseMoveRef);
        document.removeEventListener('mouseup', this._mouseUpRef);
        document.removeEventListener('touchmove', this._touchMoveRef);
        document.removeEventListener('touchend', this._touchEndRef);

        // Remove the iframe-blocking overlay
        if (this._dragOverlay && this._dragOverlay.parentNode) {
            document.body.removeChild(this._dragOverlay);
            this._dragOverlay = null;
        }

        document.body.style.userSelect = '';
        document.body.style.cursor = '';

        // Re-enable CSS transitions on chatbot container
        if (this._dragContainer) {
            this._dragContainer.style.transition = '';
            this._dragContainer = null;
        }

        // Re-enable CSS transitions on all page containers
        for (const selector of this.PAGE_CONTAINER_SELECTORS) {
            const el = document.querySelector(selector) as HTMLElement | null;
            if (el) el.style.transition = '';
        }

        if (this._dragLandingPage) {
            this._dragLandingPage = null;
        }

        // Enter Angular zone once to sync the component state
        this.ngZone.run(() => {
            this.isDragging = false;
            this.chatbotStateService.setWidth(this.panelWidth);
        });
    }

    handleSelectedEvent = (value: any): void => {
        const summaryOption = this.field1?.options?.optionsList?.find((opt: any) => opt.value === 2);
        if (value === 1) {
            this.showChat = true;
            if (summaryOption) summaryOption.iconName = 'ai-icon-unselected';
            window.dispatchEvent(new CustomEvent('chatbotOpened'));
            // Embed QuickSight chat when "Ask Hestia" is selected
            setTimeout(() => this.embedQuickSightChat(), 0);
        } else if (value === 2) {
            this.showChat = false;
            if (summaryOption) summaryOption.iconName = 'ai-icon-selected';
            window.dispatchEvent(new CustomEvent('chatbotClosed'));
        }
    };

    reviewNotes(summaryText?: string): void {
        this.getReviewNotesFields().then(() => {
            const noteField = this.reviewNotesField.options.optionsList.find((f: any) => f.name === 'note');
            if (noteField) noteField.value = summaryText ?? this.callSummaryText;
            this.saveEditionDoneMap = {};
            this.savedEditions = [];
            this.savedEditionValues = {};
            this.reviewNotesForm.clear();
            this.sidePanelService.open({
                form: this.reviewNotesForm,
                contentTemplate: this.reviewPanelContent,
                footerTemplate: this.reviewPanelFooter,
                data: {},
            });
        });
    }

    sendChatMessage(): void {
        if (!this.currentInput.trim() || this.isChatLoading) return;

        const userMessage = this.currentInput.trim();
        this.chatService.addMessage({ text: userMessage, isUser: true });
        this.currentInput = '';
        this.isChatLoading = true;
        this.shouldScroll = true;

        this.chatService.sendMessage(this.sessionId, userMessage).subscribe({
            next: (response) => {
                this.messages.push({ text: response.briefAnswer, isUser: false });
                this.isChatLoading = false;
                this.shouldScroll = true;
            },
            error: (error) => {
                console.error('Chat error:', error);
                this.chatService.addMessage({
                    text: error.message || 'Sorry, I encountered an error. Please try again.',
                    isUser: false,
                });
                this.isChatLoading = false;
                this.shouldScroll = true;
            },
        });
    }

    sendSummaryMessage(): void {
        if (!this.currentInput.trim() || this.isSummaryLoading) return;

        const userMessage = this.currentInput.trim();
        this.callSummaryMessages.push({ text: userMessage, isUser: true });
        this.currentInput = '';
        this.isSummaryLoading = true;
    }

    private initializeMessages(): void {
        this.chatService.getMessages().subscribe((message) => {
            this.messages = message;
        });
    }

    private initializeChoiceChipField(): void {
        const optionsList: any[] = [];

        // Only show the "Ask Hestia" (QuickSight chat) option when enabled via config.
        if (this.config.showAskHestiaChatWidget) {
            optionsList.push({ caption: 'Ask Hestia', id: 'Chat', value: 1, iconName: 'chat-round', disabled: false });
        }

        optionsList.push({
            caption: 'Summary',
            id: 'Call Summary',
            value: 2,
            iconName: 'ai-icon-selected',
        });

        this.field1 = {
            name: 'ChoiceChips',
            value: 2,
            options: {
                optionsList,
            },
            type: 'choice-chip',
            disabled: false,
            additionalContext: { isStretch: true, isDynamicLeft: true },
        };
    }

    private scrollToBottom(): void {
        const messagesArea = document.querySelector('.message-area-outer-wrap');
        if (messagesArea) {
            messagesArea.scrollTo({ top: messagesArea.scrollHeight, behavior: 'smooth' });
        }
    }

    private checkAmazonConnectWidget(): void {
        // No-op: Amazon Connect widget replaced by QuickSight Chat
    }

    private setupWidgetObserver(): void {
        // No-op: Amazon Connect widget observer no longer needed
    }

    /**
     * Embeds the QuickSight chat agent into the container when "Ask Hestia" is selected.
     */
    private async embedQuickSightChat(): Promise<void> {
        const container = document.getElementById('quicksight-chat-container');
        if (!container) {
            console.warn('[QuickSight Chat] Container element not found');
            this.showReloadButton = true;
            return;
        }

        try {
            this.showReloadButton = false;
            this.isChatLoading = true;
            await this.quickSightChatService.embedChat(container);
            this.isChatLoading = false;
        } catch (error) {
            console.error('[QuickSight Chat] Failed to embed:', error);
            this.isChatLoading = false;
            this.showReloadButton = true;
        }
    }

    /**
     * Retry embedding the QuickSight chat (triggered by reload button click).
     */
    retryQuickSightChat(): void {
        this.embedQuickSightChat();
    }

    private getCategoryCaption(value: string): string {
        return (
            this.sidePanelField?.options?.optionsList?.[0]?.options?.optionsList?.find(
                (opt: any) => opt.value === value
            )?.caption || value
        );
    }

    private getSubCategoryCaption(value: string): string {
        const categories = this.sidePanelField?.options?.optionsList?.[0]?.options?.optionsList || [];
        for (const cat of categories) {
            const subCat = cat.actions?.[0]?.params?.optionsList?.find((opt: any) => opt.value === value);
            if (subCat) return subCat.caption;
        }
        return value;
    }

    generateSummary(): void {
        if (!this.contactId || this.isSummaryLoading) {
            console.log('Summary generation skipped:', { contactId: this.contactId, isLoading: this.isSummaryLoading });
            return;
        }
        this.isSummaryLoading = true;

        this.callSummaryService.getCallSummary(this.contactId, this.channel).subscribe({
            next: (response) => {
                const responseBody =
                    typeof response?.body === 'string' ? JSON.parse(response.body) : response?.body ?? response;
                const summary = this.callSummaryService.extractSummaryText(response);
                const summaryText =
                    summary || responseBody?.attributes?.escalationSummary || 'No summary available for this call.';
                this.connectService.callSummary = summaryText;
                const now = new Date();
                const timestamp = this.formatTimestamp(now);
                const rawCallerNumber = responseBody?.callerNumber ?? responseBody?.attributes?.callerNumber;
                const callNumber = rawCallerNumber || responseBody?.attributes?.callBackNumber || null;
                this.generatedSummaries.push({
                    text: summaryText,
                    timestamp,
                    callerNumber: rawCallerNumber,
                    callNumber,
                    attributes: {
                        ...responseBody?.attributes,
                        contactChannel: responseBody?.channel || this.channel,
                        sentiment: responseBody?.sentiment?.overall?.customer,
                        persona: responseBody?.Persona ?? responseBody?.attributes?.Persona ?? null,
                    },
                });
                this.summaryGenerated = true;
                this.isSummaryLoading = false;

                if (summaryText) {
                    this.callIntentService.isIntentLoading.set(true);
                    this.router.navigate(['/call-intent'], { state: { customerIntent: [] } });
                    this.callIntentService.getCallIntent(summaryText).subscribe({
                        next: (response) => {
                            const responseBody =
                                typeof response?.body === 'string'
                                    ? JSON.parse(response.body)
                                    : response?.body ?? response;
                            const customerIntent = this.parseCustomerIntent(responseBody?.customerIntent);
                            this.callIntentService.lastCustomerIntent = customerIntent;
                            this.callIntentService.isIntentLoading.set(false);
                        },
                        error: (err) => {
                            this.callIntentService.isIntentLoading.set(false);
                        },
                    });
                }
            },
            error: (error) => {
                const now = new Date();
                this.generatedSummaries.push({
                    text: 'Failed to load call summary. Please try again.',
                    timestamp: `${now.toLocaleDateString()} | ${now.toLocaleTimeString()}`,
                    attributes: null,
                });
                this.isSummaryLoading = false;
            },
        });
    }
    private formatTimestamp(date: Date): string {
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        const yyyy = date.getFullYear();
        const hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const hh = String(hours % 12 || 12).padStart(2, '0');
        return `${mm}-${dd}-${yyyy} | ${hh}:${minutes} ${ampm}`;
    }

    fetchCallIntent(): void {
        // const latestSummary = this.generatedSummaries[this.generatedSummaries.length - 1]?.text;
        // if (!latestSummary) return;
        // this.router.navigate(['/call-intent']);
        this.router.navigate(['/call-intent'], {
            state: { customerIntent: this.callIntentService.lastCustomerIntent },
        });
    }

    isSaveEnabled(): boolean {
        return this.recentPolicyNumbers.length > 0 && this.recentPolicyNumbers.every((p) => this.saveEditionDoneMap[p]);
    }
    // getLobForPolicy(policyNumber: string): string {
    //     let lob = '';
    //     this.store
    //         .select((state: any) => state.policy.data)
    //         .pipe(take(1))
    //         .subscribe((data) => {
    //             lob = data?.[policyNumber]?.policySnapshot?.policyGeneralInformation?.lineOfBusiness?.value || '';
    //         });
    //     return lob ? `${lob} ${policyNumber}` : `Policy ${policyNumber}`;
    // }
    getLobForPolicy(policyNumber: string): string {
        const lob =
            this.recentPolicies.find((p) => p.policyNumber.toLowerCase() === policyNumber.toLowerCase())
                ?.lineOfBusiness || '';
        return lob ? `${lob} ${policyNumber}` : `Policy ${policyNumber}`;
    }

    onSaveEdition(): void {
        const policyControl = this.reviewNotesForm.controls[0]?.get('policy');
        const categoryControl = this.reviewNotesForm.controls[0]?.get('category');
        const subCategoryControl = this.reviewNotesForm.controls[0]?.get('subCategory');
        const noteControl = this.reviewNotesForm.controls[0]?.get('note');
        const policyValue = policyControl?.value;
        const categoryValue = categoryControl?.value;
        const subCategoryValue = subCategoryControl?.value;
        const noteValue = noteControl?.value;
        if (policyValue && categoryValue && subCategoryValue && noteValue) {
            this.savedEditionValues = {
                ...this.savedEditionValues,
                [policyValue]: { note: noteValue, category: categoryValue, subCategory: subCategoryValue },
            };
            this.saveEditionDoneMap = { ...this.saveEditionDoneMap, [policyValue]: true };
            if (!this.savedEditions.includes(policyValue)) {
                this.savedEditions = [...this.savedEditions, policyValue];
            }
        }
    }

    cancelReviewNotes(): void {
        this.sidePanelService.closeAll();
        this.reviewNotesForm.clear();
        this.saveEditionDoneMap = {};
        this.savedEditions = [];
        this.savedEditionValues = {};
    }

    async getReviewNotesFields(): Promise<void> {
        const res: any = await firstValueFrom(this.http.get('assets/policy_edit/ReviewNotes.json'));
        this.reviewNotesField = res.data.fields[0];
        const policyField = this.reviewNotesField.options.optionsList.find((f: any) => f.name === 'policy');
        if (policyField) {
            policyField.options.optionsList = this.recentPolicies.map((p) => ({
                caption: `Policy ${p.policyNumber}`,
                value: p.policyNumber,
            }));
        }
    }

    onSaveReviewNotes(): void {
        const successMessage =
            this.savedEditions.length === 1 ? 'The note was successfully generated.' : 'Notes saved successfully.';
        this.loadingService.showLoading();
        const latestSummary = this.generatedSummaries[this.generatedSummaries.length - 1];

        const customerNumber =
            latestSummary?.callerNumber ||
            latestSummary?.callNumber ||
            this.connectService.attributes?.CustomerPhoneNumber ||
            this.connectService.attributes?.CustomerNumber ||
            null;
        const commentSubject = customerNumber ? `New Notes - ${customerNumber}` : 'New Notes';

        // Snapshot before any async work
        const editionsSnapshot = [...this.savedEditions];
        const valuesSnapshot = { ...this.savedEditionValues };

        const lookups = editionsSnapshot.map((policyNumber) =>
            this.http.post<any>(this.config.quoteSearchApi, {
                InputType: 'Policy',
                search: policyNumber,
                token: getAuthToken(),
            })
        );

        forkJoin(lookups)
            .pipe(
                switchMap((results) => {
                    const notesRequests = results.map((res: any, i: number) => {
                        const policyNumber = editionsSnapshot[i];
                        const savedValues = valuesSnapshot[policyNumber];
                        const hits: any[] = res?.hits ?? [];
                        const latest = hits.sort((a, b) => Number(b.Term) - Number(a.Term))[0];
                        const quoteId = latest?.QuoteID ?? '';
                        const effectiveDate = latest?.EffectiveDate ?? '';
                        const expireDate = latest?.EndDate ?? '';

                        const body = {
                            policyId: quoteId,
                            policyEffectiveDate: effectiveDate,
                            commentTypeCode: 'CI',
                            commentText: savedValues.note,
                            commentSubject,
                            commentCategory: savedValues.category,
                            commentSubCategory: savedValues.subCategory,
                            action: 'ADD_NOTES',
                        };
                        return this.notesService.addNotes(quoteId, body).pipe(
                            map(() => ({ policyNumber, effectiveDate, expireDate })) // ← pass dates through
                        );
                    });
                    return forkJoin(notesRequests);
                })
            )
            .subscribe({
                next: (results: any[]) => {
                    results.forEach(({ policyNumber, effectiveDate, expireDate }) =>
                        this.store.dispatch(
                            refreshDCPolicy({
                                policyNumber,
                                effectiveDate: effectiveDate || undefined,
                                expireDate: expireDate || undefined,
                            })
                        )
                    );

                    this.loadingService.hideLoading();
                    this.toastService.add({ type: 'success', title: '', message: successMessage });
                    this.sidePanelService.closeAll();
                    this.reviewNotesForm.clear();
                    this.saveEditionDoneMap = {};
                    this.savedEditions = [];
                    this.savedEditionValues = {};
                },
                error: () => {
                    this.loadingService.hideLoading();
                    this.toastService.add({ type: 'error', title: '', message: 'Failed to save notes.' });
                },
            });
    }
}
